import React from 'react';
import { Box, Typography, IconButton, Breadcrumbs, Link, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

/**
 * Consistent page header: optional back button, title, subtitle, breadcrumbs,
 * and a right-aligned actions slot. Presentation only — no data fetching or
 * navigation side effects beyond the provided onBack callback.
 *
 * breadcrumbs: [{ label, onClick? }]
 */
const PageHeader = ({
  title,
  subtitle,
  onBack,
  breadcrumbs,
  actions,
  icon,
  sx,
}) => {
  return (
    <Box sx={{ mb: 3, ...sx }}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}
          sx={{ mb: 1, fontSize: '0.8125rem' }}
        >
          {breadcrumbs.map((crumb, idx) =>
            crumb.onClick && idx !== breadcrumbs.length - 1 ? (
              <Link
                key={idx}
                component="button"
                type="button"
                underline="hover"
                color="text.secondary"
                onClick={crumb.onClick}
                sx={{ fontSize: '0.8125rem', fontWeight: 500 }}
              >
                {crumb.label}
              </Link>
            ) : (
              <Typography key={idx} color="text.primary" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                {crumb.label}
              </Typography>
            )
          )}
        </Breadcrumbs>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          {onBack && (
            <Tooltip title="Back">
              <IconButton
                onClick={onBack}
                size="small"
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  color: 'text.secondary',
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: 'rgba(30, 115, 190, 0.08)',
                color: 'primary.main',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: 'break-word' }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {actions && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PageHeader;
